import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, Chip, Divider, IconButton, List, ListItem, ListItemSecondaryAction, ListItemText, Stack, Tooltip, Typography, TextField, FormControl, Select, MenuItem, InputLabel, Tabs, Tab } from '@mui/material'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import DoneIcon from '@mui/icons-material/Done'
import DeleteIcon from '@mui/icons-material/Delete'
import NotificationsIcon from '@mui/icons-material/Notifications'
import { api } from 'src/api/endpoints'
import dayjs from 'dayjs'
import Layout from 'src/components/layout/Layout'
import { useSelector, useDispatch } from 'react-redux'
import { fetchNotifications, markNotificationRead, markAllNotificationsRead, deleteNotification as deleteNotificationAction, createNotification } from 'src/redux/actions/Notifications'
import { useTranslation } from 'react-i18next'

const Notifications = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { notifications, allNotifications, loading } = useSelector(state => state.Notifications)
  const [creating, setCreating] = useState(false)
  const [targetType, setTargetType] = useState('admins') // 'admins' | 'allUsers' | 'user'
  const [newContent, setNewContent] = useState('')
  const [newUserId, setNewUserId] = useState('')
  const [createError, setCreateError] = useState(null)
  const [tabValue, setTabValue] = useState('my')
  const userRole = useSelector(state => state.User?.user?.role)
  const userId = useSelector(state => state.User?.user?._id || state.User?.user?.id)

  const isAdmin = userRole === 'admin'
  // For admins we keep previous logic (exclude self-authored admin-target notifications).
  // For non-admin users we simply show all fetched notifications.
  const adminNotifications = useMemo(() => notifications.filter(n => n.for === 'admin'), [notifications])
  const myAdminNotifications = useMemo(() => {
    if (!isAdmin) return []
    return adminNotifications.filter(n => {
      const createdById = n?.createdBy && (n.createdBy._id || n.createdBy)
      // show notifications created by others or system (no createdBy)
      return !createdById || String(createdById) !== String(userId)
    })
  }, [adminNotifications, userId, isAdmin])
  const displayedNotifications = useMemo(() => {
    return isAdmin ? myAdminNotifications : notifications
  }, [isAdmin, myAdminNotifications, notifications])
  const unreadCount = useMemo(() => {
    return displayedNotifications.filter(n => !n.read).length
  }, [displayedNotifications])

  useEffect(() => {
    dispatch(fetchNotifications(userRole))
  }, [dispatch, userRole])

  const markOneRead = (id) => { dispatch(markNotificationRead(id)) }

  const markAllRead = () => { dispatch(markAllNotificationsRead()) }

  const deleteNotification = (id) => { dispatch(deleteNotificationAction(id)) }

  const handleCreateNotification = async () => {
    setCreateError(null)
    if (!newContent.trim()) {
      setCreateError(t('view.notifications.contentRequired'))
      return
    }
    if (targetType === 'user' && !newUserId.trim()) {
      setCreateError(t('view.notifications.userIdRequired'))
      return
    }
    setCreating(true)
    try {
      await dispatch(createNotification({ targetType, content: newContent, userId: newUserId }))
      setNewContent('')
      setNewUserId('')
      setTargetType('admins')
    } catch (err) {
      setCreateError(err?.response?.data?.error || t('view.notifications.failedToCreate'))
    } finally {
      setCreating(false)
    }
  }

  const showCreateTab = userRole === 'admin'

  return (
    <Layout>
    <Box sx={{ maxWidth: 1300, mx: 'auto', width: '100%' }}>
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          <NotificationsIcon />
          <Typography variant="h6">{t('view.notifications.title')}</Typography>
          <Chip size="small" color={unreadCount ? 'primary' : 'default'} label={`${t('view.notifications.unread')}: ${unreadCount}`} />
          <Chip size="small" variant="outlined" label={`${t('view.notifications.total')}: ${displayedNotifications.length}`} />
        </Stack>
        <Tooltip title={unreadCount ? t('view.notifications.markAllRead') : t('view.notifications.allRead')}>
          <span>
            <Button
              size="small"
              startIcon={<DoneAllIcon />}
              onClick={markAllRead}
              disabled={!unreadCount || loading}
              variant="contained"
            >
              {t('view.notifications.markAllRead')}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Divider />
      {showCreateTab && (
        <Tabs value={tabValue} onChange={(e,v)=>setTabValue(v)} sx={{mb:2}}>
          <Tab value="my" label={t('view.notifications.myNotifications')} />
          <Tab value="create" label={t('view.notifications.createNotification')} />
        </Tabs>
      )}

      {tabValue === 'create' && showCreateTab && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb:2 }}>
          <Typography variant="subtitle1" fontWeight={600}>{t('view.notifications.createNotification')}</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="targetTypeLabel">{t('view.notifications.target')}</InputLabel>
              <Select labelId="targetTypeLabel" label={t('view.notifications.target')} value={targetType} onChange={e => setTargetType(e.target.value)}>
                <MenuItem value="admins">{t('view.notifications.allAdmins')}</MenuItem>
                <MenuItem value="allUsers">{t('view.notifications.allUsers')}</MenuItem>
                <MenuItem value="user">{t('view.notifications.specificUser')}</MenuItem>
              </Select>
            </FormControl>
            {targetType === 'user' && (
              <TextField
                size="small"
                label={t('view.notifications.userId')}
                value={newUserId}
                onChange={e => setNewUserId(e.target.value)}
                placeholder={t('view.notifications.userIdPlaceholder')}
                sx={{ flex: 1 }}
              />
            )}
            <TextField
              size="small"
              label={t('view.notifications.content')}
              value={newContent}
              onChange={e => setNewContent(e.target.value)}
              multiline
              minRows={1}
              sx={{ flex: 2 }}
            />
            <Button
              variant="contained"
              onClick={handleCreateNotification}
              disabled={creating}
            >
              {creating ? t('view.notifications.creating') : t('view.notifications.create')}
            </Button>
          </Stack>
          {createError && (
            <Typography variant="caption" color="error">{createError}</Typography>
          )}
          <Divider />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{t('view.notifications.allNotifications')}</Typography>
          <List dense disablePadding>
            {allNotifications.length === 0 && (
              <Box sx={{ py: 3, textAlign: 'center', opacity: 0.7 }}>
                <Typography variant="caption">{t('view.notifications.noNotifications')}</Typography>
              </Box>
            )}
            {allNotifications.map(n => (
              <ListItem key={n._id} divider sx={{ alignItems: 'flex-start' }}>
                <ListItemText
                  primary={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle2" sx={{ fontWeight: n.read ? 400 : 600 }}>
                        {n.content}
                      </Typography>
                      {n.for === 'strict'
                        ? (n.user && (
                            <Chip
                              size="small"
                              variant="outlined"
                              color="secondary"
                              label={n.user.fullname || n.user.email || 'user'}
                            />
                          ))
                        : (n.for && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={n.for}
                            />
                          ))}
                      {n.createdBy && (
                        <Chip size="small" color="default" variant="outlined" label={`${t('view.notifications.by')} ${n.createdBy.fullname || n.createdBy.email || 'unknown'}`} />
                      )}
                      {!n.read && <Chip size="small" color="primary" label={t('view.notifications.new')} />}
                    </Stack>
                  }
                  secondary={
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {dayjs(n.createdAt).format('YYYY-MM-DD HH:mm')}
                    </Typography>
                  }
                />
                <ListItemSecondaryAction>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title={n.read ? t('view.notifications.alreadyRead') : t('view.notifications.markAsRead')}>
                      <span>
                        <IconButton
                          edge="end"
                          onClick={() => markOneRead(n._id)}
                          disabled={n.read}
                          color={n.read ? 'default' : 'primary'}
                          size="small"
                        >
                          <DoneIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {userRole === 'admin' && (
                      <Tooltip title={t('view.notifications.deleteNotification')}>
                        <span>
                          <IconButton
                            edge="end"
                            onClick={() => deleteNotification(n._id)}
                            
                            color="error"
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {tabValue === 'my' && (
      <List dense disablePadding>
        {displayedNotifications.length === 0 && (
          <Box sx={{ py: 6, textAlign: 'center', opacity: 0.7 }}>
            <Typography variant="body2">{loading ? t('view.notifications.loading') : t('view.notifications.noNotificationsForYou')}</Typography>
          </Box>
        )}
        {displayedNotifications.map((n) => (
          <ListItem key={n._id} divider sx={{ alignItems: 'flex-start' }}>
            <ListItemText
              primary={
                <Stack direction="row" alignItems="center" spacing={1}>
                  {!n.read && <Chip size="small" color="primary" label={t('view.notifications.new')} />}
                  <Typography variant="subtitle1" sx={{ fontWeight: n.read ? 400 : 600}}>
                    {n.content}
                  </Typography>
                </Stack>
              }
              secondary={
                <>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {dayjs(n.createdAt).format('YYYY-MM-DD HH:mm')}
                  </Typography>
                </>
              }
            />
            <ListItemSecondaryAction>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title={n.read ? t('view.notifications.alreadyRead') : t('view.notifications.markAsRead')}>
                  <span>
                    <IconButton
                      edge="end"
                      onClick={() => markOneRead(n._id)}
                      disabled={n.read}
                      color={n.read ? 'default' : 'primary'}
                      size="small"
                    >
                      <DoneIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                {userRole === 'admin' && (
                  <Tooltip title={t('view.notifications.deleteNotification')}>
                    <span>
                      <IconButton
                        edge="end"
                        onClick={() => deleteNotification(n._id)}
                      
                        color="error"
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
      )}
    </Box>
    </Box>
    </Layout>
  )
}

export default Notifications
